import React, { useState, useEffect } from 'react';
import { 
  Settings, 
  Sparkles, 
  CheckCircle2, 
  Trash2, 
  GripVertical, 
  AlertCircle,
  Cpu,
  BrainCircuit,
  MessageSquare,
  Power,
  ArrowUp,
  ArrowDown,
  RefreshCw
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { useFirebase } from './FirebaseProvider';
import { getSystemSettings, updateSystemSettings } from '../services/settingsService';
import { SystemSettings, AIProviderConfig, AIProviderId } from '../types';

export const SystemSettingsPanel: React.FC = () => {
  const { user, effectiveUserId } = useFirebase();
  const [settings, setSettings] = useState<SystemSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ text: string, type: 'success' | 'error' } | null>(null);

  useEffect(() => {
    if (effectiveUserId) {
      loadSettings();
    }
  }, [effectiveUserId]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const data = await getSystemSettings(effectiveUserId!);
      setSettings(data);
    } catch (error) {
      console.error("Error loading settings:", error);
      setMessage({ text: "Error al cargar la configuración", type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleProvider = (providerId: AIProviderId) => {
    if (!settings) return;

    const newPipeline = settings.ai.pipeline.map(p => 
      p.providerId === providerId ? { ...p, enabled: !p.enabled } : p
    );

    setSettings({
      ...settings,
      ai: { ...settings.ai, pipeline: newPipeline }
    });
  };

  const handleChangePriority = (index: number, direction: 'up' | 'down') => {
    if (!settings) return;

    const newPipeline = [...settings.ai.pipeline];
    if (direction === 'up' && index > 0) {
      [newPipeline[index], newPipeline[index - 1]] = [newPipeline[index - 1], newPipeline[index]];
    } else if (direction === 'down' && index < newPipeline.length - 1) {
      [newPipeline[index], newPipeline[index + 1]] = [newPipeline[index + 1], newPipeline[index]];
    }

    // Update priorities based on new order
    const updatedPipeline = newPipeline.map((p, idx) => ({ ...p, priority: idx + 1 }));

    setSettings({
      ...settings,
      ai: { ...settings.ai, pipeline: updatedPipeline }
    });
  };

  const handleSave = async () => {
    if (!effectiveUserId || !settings) return;

    try {
      setSaving(true);
      setMessage(null);
      await updateSystemSettings(effectiveUserId, settings);
      setMessage({ text: "Configuración guardada correctamente", type: "success" });
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error("Error saving settings:", error);
      setMessage({ text: "Error al guardar la configuración", type: "error" });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="animate-spin text-zinc-500" size={32} />
      </div>
    );
  }

  const enabledProvidersCount = settings?.ai.pipeline.filter(p => p.enabled).length || 0;

  return (
    <div className="max-w-3xl mx-auto space-y-6 pb-20">
      <div className="flex items-center gap-3 mb-2">
        <div className="p-2 bg-zinc-800 rounded-xl border border-zinc-700">
          <Settings className="text-zinc-400" size={20} />
        </div>
        <h2 className="text-xl font-bold text-white">Configuración del Sistema</h2>
      </div>

      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-zinc-900 border border-zinc-800 rounded-3xl overflow-hidden shadow-2xl"
      >
        <div className="p-8 border-b border-zinc-800">
          <div className="flex items-center gap-4 mb-2">
            <div className="w-12 h-12 bg-indigo-500/10 rounded-2xl flex items-center justify-center border border-indigo-500/20">
              <BrainCircuit className="text-indigo-400" size={24} />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">Pipeline de Inteligencia Artificial</h3>
              <p className="text-xs text-zinc-500">Configura los proveedores y el orden de ejecución.</p>
            </div>
          </div>
        </div>

        <div className="p-8 space-y-6">
          {enabledProvidersCount === 0 && (
            <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-2xl flex items-start gap-4">
              <AlertCircle className="text-amber-500 shrink-0 mt-0.5" size={18} />
              <p className="text-xs text-amber-200/80 leading-relaxed">
                Atención: No tienes ningún proveedor habilitado. Las funciones de entrada inteligente no estarán disponibles.
              </p>
            </div>
          )}

          <div className="space-y-3">
            <label className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest px-1">
              Proveedores de IA disponibles (Orden de prioridad)
            </label>
            
            <div className="space-y-2">
              {settings?.ai.pipeline.map((provider, idx) => (
                <div 
                  key={provider.providerId}
                  className={`flex items-center gap-4 p-4 rounded-2xl border transition-all ${
                    provider.enabled ? 'bg-zinc-800/50 border-zinc-700 shadow-lg' : 'bg-zinc-950 border-zinc-800 opacity-60'
                  }`}
                >
                  <div className="flex flex-col gap-1">
                    <button 
                      onClick={() => handleChangePriority(idx, 'up')}
                      disabled={idx === 0}
                      className="p-1 hover:bg-zinc-700 rounded text-zinc-500 disabled:opacity-0"
                    >
                      <ArrowUp size={14} />
                    </button>
                    <button 
                      onClick={() => handleChangePriority(idx, 'down')}
                      disabled={idx === settings.ai.pipeline.length - 1}
                      className="p-1 hover:bg-zinc-700 rounded text-zinc-500 disabled:opacity-0"
                    >
                      <ArrowDown size={14} />
                    </button>
                  </div>

                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center border ${
                    provider.providerId === 'gemini' 
                      ? 'bg-blue-500/10 text-blue-400 border-blue-500/20' 
                      : 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                  }`}>
                    {provider.providerId === 'gemini' ? <Sparkles size={20} /> : <MessageSquare size={20} />}
                  </div>

                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h4 className="font-bold text-white capitalize">{provider.providerId}</h4>
                      {idx === 0 && provider.enabled && (
                        <span className="text-[8px] bg-indigo-500/20 text-indigo-400 px-1.5 py-0.5 rounded-full border border-indigo-500/20 font-black tracking-widest uppercase">
                          Por Defecto
                        </span>
                      )}
                    </div>
                    <p className="text-[10px] text-zinc-500 italic">
                      {provider.providerId === 'gemini' ? 'Google Gemini 1.5 Flash' : 'OpenAI GPT-4o mini'}
                    </p>
                  </div>

                  <button
                    onClick={() => handleToggleProvider(provider.providerId)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-xl border text-[10px] font-black uppercase tracking-widest transition-all ${
                      provider.enabled 
                        ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20 hover:bg-emerald-500/20' 
                        : 'bg-zinc-800 text-zinc-500 border-zinc-700 hover:bg-zinc-700'
                    }`}
                  >
                    <Power size={12} />
                    {provider.enabled ? 'Habilitado' : 'Deshabilitado'}
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="p-4 bg-zinc-950 rounded-2xl border border-zinc-800">
            <h4 className="text-xs font-bold text-zinc-400 mb-2 flex items-center gap-2">
              <Cpu size={14} className="text-indigo-400" />
              ¿Cómo funciona el pipeline?
            </h4>
            <p className="text-[10px] text-zinc-500 leading-relaxed">
              El sistema siempre intentará procesar tus solicitudes primero con el proveedor marcado como <b>Por Defecto</b>. 
              Si ese proveedor falla por saturación o errores externos, el sistema probará automáticamente con el siguiente proveedor habilitado en la lista. 
              Este proceso continúa hasta que una petición es exitosa o se agotan los proveedores.
            </p>
          </div>

          <div className="border-t border-zinc-800 pt-6">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-10 h-10 bg-red-500/10 rounded-xl flex items-center justify-center border border-red-500/20">
                <Power className="text-red-400" size={20} />
              </div>
              <div>
                <h4 className="font-bold text-white">Fecha de Cierre Contable y Financiero</h4>
                <p className="text-[10px] text-zinc-500 italic">
                  Bloquea la edición o eliminación de operaciones con fecha igual o anterior a esta.
                </p>
              </div>
            </div>
            <div className="space-y-2 max-w-xs">
              <input
                type="date"
                value={settings?.closingDate || ''}
                onChange={(e) => setSettings(settings ? { ...settings, closingDate: e.target.value } : null)}
                className="w-full bg-zinc-950 border border-zinc-800 rounded-xl p-3 text-white focus:outline-none focus:border-indigo-500/50 focus:ring-1 focus:ring-indigo-500/50"
              />
            </div>
          </div>
        </div>

        <div className="p-8 bg-zinc-950/50 border-t border-zinc-800 flex items-center justify-between">
          <AnimatePresence>
            {message && (
              <motion.div 
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className={`flex items-center gap-2 text-xs font-bold uppercase tracking-wider ${
                  message.type === 'success' ? 'text-emerald-500' : 'text-red-500'
                }`}
              >
                {message.type === 'success' ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
                {message.text}
              </motion.div>
            )}
          </AnimatePresence>
          <div className="flex-1" />
          <button
            onClick={handleSave}
            disabled={saving || !settings}
            className="px-8 py-3 bg-zinc-100 hover:bg-white text-black font-black rounded-2xl text-[11px] uppercase tracking-widest transition-all disabled:opacity-50 shadow-lg shadow-white/5 flex items-center gap-2"
          >
            {saving ? <RefreshCw className="animate-spin" size={16} /> : <Settings size={16} />}
            Guardar Cambios
          </button>
        </div>
      </motion.div>
    </div>
  );
};
